import React, { Fragment, useRef } from 'react';
import { css } from '@compiled/react';

import AppIcon from '@atlaskit/icon/core/app';
import Image from '@atlaskit/image';
import type { ExtensionConfiguration } from '@atlaskit/editor-plugin-selection-extension';

import { FORGE_MODULE_CONTEXT_MENU } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import { useExtensionList } from '@confluence/forge-ui/entry-points/useExtensionList.ts';
import { extensionTitle } from '@confluence/forge-ui/entry-points/extensionTitle.ts';
import { ACTION_PANEL } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { useWebItemLocation } from '@confluence/web-item-location/entry-points/useWebItemLocation.ts';

import { DROPDOWN_EXCLUDED_ITEMS } from '../HighlightActions.tsx';

import { ForgeEditorAction } from './ForgeEditorAction.tsx';

const iconImageStyle = css({
	height: '24px',
	width: '24px',
});

// Need this to satisfy the toolbar extension API which expects a label prop type
const ImageIcon = ({ label, src }: { label: string | undefined; src?: string }) => (
	<Fragment>
		<Image css={iconImageStyle} src={src} alt={label} />
	</Fragment>
);

// Used to fetch extensions in new editor toolbar
export const useEditorToolbarExtensions = (
	contentId: string | undefined,
): ExtensionConfiguration[] => {
	// Need ref since these are fetched and updated asynchronously so the API uses a ref to track this
	const extensionsRef = useRef<any[]>([]);

	return [
		{
			key: 'external-extensions',
			source: 'external',
			inlineToolbar: {
				getMenuItems: () => {
					// Hooks called inside callback for lazy loading - only fetches when menu opens and not on editor load.
					// Safe because hooks execute in same order and GraphQL cache prevents duplicate requests.
					// eslint-disable-next-line react-hooks/rules-of-hooks
					const { extensions } = useExtensionList({
						moduleType: FORGE_MODULE_CONTEXT_MENU,
						queryOptions: {
							fetchPolicy: 'cache-first',
						},
					});

					// eslint-disable-next-line react-hooks/rules-of-hooks
					const { webItems } = useWebItemLocation({
						contentId,
						location: ACTION_PANEL,
					});

					const filteredWebItems = webItems.filter(
						(item) => !DROPDOWN_EXCLUDED_ITEMS.includes(item.completeKey || ''),
					);

					const connectItems = filteredWebItems.map((item) => ({
						label: item?.label,
						icon: item?.icon?.path
							? () => <ImageIcon label={item?.label || ''} src={item?.icon?.path} />
							: AppIcon,
						onClick: () => {
							if (item?.url) {
								window.open(item.url, '_blank');
							}
						},
					}));

					const forgeItems = extensions.map((extension) => ({
						label: extensionTitle(extension),
						icon: AppIcon,
						contentComponent: ({ selection, closeExtension }) => {
							return (
								<ForgeEditorAction
									key={extension.key}
									contentId={contentId}
									forgeExtension={extension}
									onClose={closeExtension}
									selection={selection}
								/>
							);
						},
					}));

					extensionsRef.current = [...connectItems, ...forgeItems];

					return [...extensionsRef.current];
				},
			},
		},
	];
};
